import React, { useMemo } from 'react';
import { Pressable, StatusBar } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import GuardianInvitationErrorIcon from '@/assets/icons/ic-guardian-invitation-error.svg';
import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { GuardianInvitationErrorReason } from '@/features/guardian/types/invitation';
import { createStyles, useScaling } from '@/hooks';
import { googleSignOut } from '@/libs/firebase/auth';
import type { RootStackScreenProps } from '@/navigations/types';
import { useSessionStore } from '@/stores';
import { colors, guardianColors } from '@/theme/colors';
import { DATE_FORMATS } from '@/utils/date-formats';
import { format, isValid, parseISO } from 'date-fns';

const inviteCopy = STRINGS.guardian.coGuardianInvite;
const copy = inviteCopy.error;
type ErrorParams = RootStackScreenProps<'GuardianInvitationError'>['route']['params'];

type ErrorContent = {
  title: string;
  message?: string;
  accountMessage?: string;
  detail?: string;
  help?: string;
  actionLabel: string;
};

function formatExpiryDate(value?: string) {
  if (!value) return '';
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, DATE_FORMATS.TEXT) : value;
}

function getErrorContent(params: ErrorParams): ErrorContent {
  const { reason, invitedEmail, inviterName, playerName, expiryDate } = params;
  const accountMessage = invitedEmail ? copy.sentTo : undefined;

  switch (reason) {
    case GuardianInvitationErrorReason.Expired: {
      const formattedExpiry = formatExpiryDate(expiryDate);
      return {
        title: copy.expiredTitle,
        accountMessage,
        detail: formattedExpiry ? copy.expiredOn(formattedExpiry) : copy.expiredFallback,
        help:
          inviterName && playerName
            ? copy.expiredHelp(inviterName, playerName)
            : copy.expiredHelpFallback,
        actionLabel: copy.goHome,
      };
    }
    case GuardianInvitationErrorReason.EmailMismatch:
      return {
        title: copy.incorrectAccountTitle,
        accountMessage,
        message: invitedEmail ? copy.accountMessage : copy.accountFallback,
        actionLabel: copy.logoutAndLogin,
      };
    case GuardianInvitationErrorReason.LoginRequired:
      return {
        title: copy.loginRequiredTitle,
        accountMessage,
        message: invitedEmail ? copy.accountMessage : copy.accountFallback,
        actionLabel: copy.login,
      };
    case GuardianInvitationErrorReason.Unknown:
      return {
        title: copy.unknownTitle,
        message: copy.unknownMessage,
        actionLabel: copy.goHome,
      };
    case GuardianInvitationErrorReason.AlreadyProcessed:
    case GuardianInvitationErrorReason.InvalidLink:
    default:
      return {
        title: copy.invalidTitle,
        accountMessage,
        message: copy.invalidMessage,
        actionLabel: copy.goHome,
      };
  }
}

export function GuardianInvitationErrorScreen({
  navigation,
  route,
}: RootStackScreenProps<'GuardianInvitationError'>) {
  const styles = useStyles();
  const { isTablet, scale } = useScaling();
  const insets = useSafeAreaInsets();
  const isAuthenticated = useSessionStore(state => state.isAuthenticated);
  const content = useMemo(() => getErrorContent(route.params), [route.params]);
  const { reason, invitedEmail } = route.params;

  const handlePress = async () => {
    if (reason === GuardianInvitationErrorReason.EmailMismatch) {
      // Soft logout — the same pair the Profile "Sign out" uses: clear the Google One Tap cache
      // and flag the session as logged out, without tearing down Firebase auth. A hard
      // `signOut()` here fired `onAuthStateChanged(null)` while the guardian tabs were still
      // mounted (this screen sits on the root stack, over them), so the notification badge
      // query refetched with the session already gone — two `/notifications` 401s and a
      // "Missing bearer token" alert. Signing in from the Login screen replaces the session.
      googleSignOut();
      useSessionStore.getState().setSoftLoggedOut(true);
      navigation.reset({
        index: 0,
        routes: [{ name: 'UnAuth', params: { screen: 'Login' } }],
      });
      return;
    }

    if (reason === GuardianInvitationErrorReason.LoginRequired) {
      navigation.replace('UnAuth', { screen: 'Login' });
      return;
    }

    if (isAuthenticated) {
      navigation.reset({
        index: 0,
        routes: [{ name: 'Guardian', params: { screen: 'GuardianDashboard' } }],
      });
      return;
    }

    navigation.reset({
      index: 0,
      routes: [{ name: 'Splash' }],
    });
  };

  return (
    <LinearGradient
      colors={['#EEF3FF', guardianColors['1'], '#EEF3FF']}
      locations={[0.08, 0.47, 0.9]}
      useAngle
      angle={210.65}
      style={styles.screen}>
      <StatusBar barStyle="dark-content" backgroundColor="transparent" translucent />
      <Stack
        flex={1}
        align="center"
        justify="center"
        paddingX={scale(16)}
        style={{ paddingTop: insets.top, paddingBottom: insets.bottom }}>
        <Stack align="center" style={styles.card} gap={scale(20)}>
          <Stack style={styles.iconOuter} padding={scale(16)} borderRadius={scale(100)}>
            <Stack style={styles.iconMiddle} padding={scale(12)} borderRadius={scale(100)}>
              <Stack
                style={styles.iconInner}
                padding={scale(10)}
                borderRadius={scale(100)}
                align="center"
                justify="center">
                <GuardianInvitationErrorIcon width={scale(31.2)} height={scale(31.2)} />
              </Stack>
            </Stack>
          </Stack>

          <Stack align="center" style={styles.content} gap={scale(isTablet ? 20 : 16)}>
            <Text
              variant={
                isTablet
                  ? 'Font Guardian App/Heading/Heading H5 24px'
                  : 'Font Guardian App/Body/Body 16 Bold'
              }
              fontSize={isTablet ? scale(20) : undefined}
              lineHeight={isTablet ? scale(28) : undefined}
              style={styles.title}>
              {content.title}
            </Text>

            {content.accountMessage && invitedEmail ? (
              <Stack align="center" style={styles.account}>
                <Text
                  variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                  style={styles.bodyText}>
                  {content.accountMessage}
                </Text>
                <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.email}>
                  {invitedEmail}
                </Text>
              </Stack>
            ) : null}

            {content.detail ? (
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                style={styles.bodyText}>
                {content.detail}
              </Text>
            ) : null}

            {content.message ? (
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                style={styles.bodyText}>
                {content.message}
              </Text>
            ) : null}

            {content.help ? (
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                style={styles.bodyText}>
                {content.help}
              </Text>
            ) : null}
          </Stack>

          <Pressable
            accessibilityRole="button"
            accessibilityLabel={content.actionLabel}
            onPress={handlePress}
            style={({ pressed }) => [styles.button, pressed && styles.pressed]}>
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
              style={styles.buttonLabel}>
              {content.actionLabel}
            </Text>
          </Pressable>

          <Stack style={styles.footer} align="center" gap={scale(8)}>
            <Stack style={styles.divider} />
            <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.copyright}>
              {inviteCopy.copyright}
            </Text>
          </Stack>
        </Stack>
      </Stack>
    </LinearGradient>
  );
}

const useStyles = createStyles(({ scale, isTablet }) => ({
  screen: { flex: 1 },
  card: {
    width: scale({ phone: 343, tablet: 664 }),
    maxWidth: '100%',
    paddingHorizontal: scale({ phone: 18, tablet: 24 }),
    paddingVertical: scale(20),
    borderWidth: isTablet ? 0 : scale(1),
    borderColor: guardianColors['3'],
    borderRadius: scale(20),
    backgroundColor: colors['0'],
  },
  iconOuter: { backgroundColor: 'rgba(255, 45, 45, 0.04)' },
  iconMiddle: { backgroundColor: 'rgba(255, 45, 45, 0.08)' },
  iconInner: {
    width: scale(64),
    height: scale(64),
    backgroundColor: 'rgba(255, 45, 45, 0.08)',
  },
  content: { width: '100%' },
  account: { width: '100%' },
  title: { width: '100%', color: guardianColors['10'], textAlign: 'center' },
  bodyText: { width: '100%', color: guardianColors['10'], textAlign: 'center' },
  email: { width: '100%', color: guardianColors['Main/Main'], textAlign: 'center' },
  button: {
    width: isTablet ? scale(344) : '100%',
    height: scale(48),
    borderRadius: scale(12),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: guardianColors['Main/Main'],
    shadowColor: '#292757',
    shadowOpacity: 0.2,
    shadowOffset: { width: 0, height: scale(1) },
    shadowRadius: scale(1),
    elevation: 1,
  },
  pressed: { opacity: 0.7 },
  buttonLabel: { color: colors['0'] },
  footer: { width: '100%' },
  divider: { width: '100%', height: scale(1), backgroundColor: guardianColors['3'] },
  copyright: { color: colors['Labels/Primary'], textAlign: 'center' },
}));
